import React, { useEffect, useMemo, useState } from "react";
import logom from "../../assets/images/icon/amanhmo.svg";
import {
  Button,
  DatePicker,
  Dropdown,
  Modal,
  notification,
  Progress,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { DownloadOutlined, PieChartOutlined, ReloadOutlined } from "@ant-design/icons";
import { usePermissions } from "../../assets/utils/usePermissions";
import { useNavigate } from "react-router-dom";
import { uri_img } from "../../assets/utils/http-request";
import lib from "../../assets/utils/lib";
import CurrencyFormat from "react-currency-format";
import { ADMIN_REVIEW_MODAL_CLASS } from "../dashboard/utils";
import { metricTagColor, calculationRuleDescription, resolveGranularTargetLabel } from "../../assets/utils/limitMetricDisplay";
import "./enrollee-consumption.css";

const { RangePicker } = DatePicker;

const PERIOD_OPTIONS = [
  { label: "Current cycle", value: "cycle" },
  { label: "Year", value: "year" },
  { label: "Quarter", value: "quarter" },
  { label: "Month", value: "month" },
  { label: "Custom", value: "custom" },
];

const getLimitColor = (remaining, limit) => {
  if (!limit || limit <= 0) return "#3497D3";
  const pct = (remaining / limit) * 100;
  if (pct > 50) return "#49B870";
  if (pct > 30) return "#faad14";
  return "#ff4d4f";
};

const formatGranularAmount = (row, value) => {
  const slug = row.metric_type;
  const unit = String(row.unit_label || "").trim();
  const num = Number(value ?? 0);
  if (slug === "monetary" || unit === "₦" || row.calculation_rule === "sum_approved_cost") {
    return <CurrencyFormat value={num} prefix="₦" thousandSeparator="," displayType="text" />;
  }
  const suffix = unit && unit !== "₦" ? ` ${unit}` : "";
  const shown = Number.isInteger(num)
    ? num.toLocaleString()
    : num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return (
    <span>
      {shown}
      {suffix}
    </span>
  );
};

function enrolleeName(data) {
  return [data?.first_name, data?.middle_name, data?.surname].filter(Boolean).join(" ") || "—";
}

/**
 * Format a value in a plan-management limit's unit: money in naira; count
 * (sessions, visits, times), days and quantity as numbers with their unit.
 * Returns null for money so the row keeps its default currency formatter.
 */
const PM_UNIT = { count: ["time", "times"], days: ["day", "days"], quantity: ["unit", "units"] };
function pmMeasureFormatter(metric) {
  if (!metric || metric === "money" || !PM_UNIT[metric]) return null;
  const [one, many] = PM_UNIT[metric];
  return (value) => {
    const n = Number(value || 0);
    return `${n.toLocaleString("en-NG", { maximumFractionDigits: 2 })} ${n === 1 ? one : many}`;
  };
}

function LimitProgressRow({ label, limit, consumed, remaining, formatValue, badge, tooltip, verb = "spent" }) {
  const hasLimit = limit !== null && limit !== undefined && Number.isFinite(Number(limit));
  const lim = Number(limit || 0);
  const con = Number(consumed || 0);
  const rem = Number.isFinite(Number(remaining)) ? Number(remaining) : Math.max(0, lim - con);
  const pct = lim > 0 ? Math.min(100, Math.round((con / lim) * 100)) : 0;
  const color = getLimitColor(rem, lim);
  const format = formatValue || ((value) => (
    <CurrencyFormat value={value} prefix="₦" thousandSeparator="," displayType="text" />
  ));
  const labelNode = (
    <span className="consumption-limit-row__label">
      {badge}
      {label}
    </span>
  );

  return (
    <div className="consumption-limit-row">
      <div className="consumption-limit-row__header">
        {tooltip ? <Tooltip title={tooltip}>{labelNode}</Tooltip> : labelNode}
        <span className="consumption-limit-row__values">
          {hasLimit ? <>{format(con)} of {format(lim)}</> : <>{format(con)} {verb}</>}
        </span>
      </div>
      {hasLimit ? (
        <>
          <Progress percent={pct} strokeColor={color} showInfo={false} size="small" />
          <div className="consumption-limit-row__footer">
            <span>Remaining: {format(rem)}</span>
            <span>{pct}% used</span>
          </div>
        </>
      ) : (
        <div className="consumption-limit-row__footer">
          <span>No limit applies</span>
        </div>
      )}
    </div>
  );
}

export const EnrolleeConsumption = ({
  userId,
  enrolleeData = null,
  triggerVariant = "link",
  triggerLabel = "View consumption",
  // "small" everywhere it sits in dense rows; the PA page uses a full-size button.
  triggerSize = "small",
  triggerClassName = "",
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [consumptionSummary, setConsumptionSummary] = useState({});
  const [period, setPeriod] = useState("cycle");
  const [customDates, setCustomDates] = useState([null, null]);

  const avatarSrc = enrolleeData?.pic_name ? `${uri_img}${enrolleeData.pic_name}` : logom;

  // Client Consumption Report (PDF / Excel) for the period shown here.
  const { hasAnyPermission } = usePermissions();
  const canDownloadReport = hasAnyPermission(["clients.consumption_report", "admin.*"]);
  const [reportSummary, setReportSummary] = useState(null);
  const [reportBusy, setReportBusy] = useState(false);

  const periodParams = () => {
    const params = { period };
    if (period === "custom" && customDates?.[0] && customDates?.[1]) {
      params.start_date = customDates[0].format("YYYY-MM-DD");
      params.end_date = customDates[1].format("YYYY-MM-DD");
    }
    return params;
  };
  const customIncomplete = period === "custom" && !(customDates?.[0] && customDates?.[1]);

  useEffect(() => {
    if (!open || !userId || !canDownloadReport || customIncomplete) {
      setReportSummary(null);
      return undefined;
    }
    let cancelled = false;
    lib.getClientConsumptionReport(userId, { ...periodParams(), summary: 1 })
      .then((resp) => {
        if (cancelled) return;
        const body = resp?.data;
        setReportSummary(body && Number(body.error) === 0 ? body.data : null);
      })
      .catch(() => { if (!cancelled) setReportSummary(null); });
    return () => { cancelled = true; };
  }, [open, period, customDates, userId, canDownloadReport]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadReport = async (format) => {
    setReportBusy(true);
    try {
      const resp = await lib.getClientConsumptionReport(userId, periodParams());
      const body = resp?.data;
      if (!body || Number(body.error) !== 0) {
        notification.error({ message: body?.message || resp?.message || "Could not load the consumption report" });
        return;
      }
      const report = body.data;
      const mod = await import("../../components/Clients/clientConsumptionReport");
      const { filename } = format === "pdf"
        ? await mod.downloadClientConsumptionPdf(report)
        : await mod.downloadClientConsumptionXlsx(report);
      notification.success({ message: `Downloaded ${filename}` });
    } catch (e) {
      notification.error({ message: e?.message || "Could not create the consumption report" });
    } finally {
      setReportBusy(false);
    }
  };
  const noReportItems = reportSummary != null && Number(reportSummary.item_count || 0) === 0;

  const handleVisitClick = () => {
    setOpen(false);
    navigate(`/checkins?insurance_no=${encodeURIComponent(userId)}`);
  };

  const loadConsumption = async (insuranceNo) => {
    setLoading(true);
    try {
      const params = { period };
      if (period === "custom" && customDates[0] && customDates[1]) {
        params.start_date = customDates[0].format("YYYY-MM-DD");
        params.end_date = customDates[1].format("YYYY-MM-DD");
      }
      const resp = await lib.getEnrolleeConsumption(insuranceNo, params);
      const data = resp?.data;
      if (data && Number(data?.error) === 0) {
        setConsumptionSummary(data?.data || {});
      } else {
        notification.error({ message: data?.message || "Failed to load consumption summary" });
      }
    } catch (e) {
      notification.error({ message: e?.message || "Failed to load consumption summary" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userId) return;
    if (open) {
      loadConsumption(userId);
    }
  }, [open, period, customDates, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openModal = (event) => {
    event?.stopPropagation?.();
    if (!userId) {
      notification.error({ message: "Unknown enrollee" });
      return;
    }
    setOpen(true);
  };

  const cycle = consumptionSummary?.cycle;
  const limits = consumptionSummary?.limits || {};
  const granularRows = Array.isArray(limits?._granular) ? limits._granular : [];
  const legacyLimitEntries = Object.entries(limits).filter(([k]) => k !== "_granular");
  const consumption = consumptionSummary?.consumption || {};
  const benefitItemRows = Array.isArray(consumptionSummary?.by_benefit_item) ? consumptionSummary.by_benefit_item : [];

  // Envelope consumption is shown ONCE per envelope: the legacy plan limit is the
  // monetary envelope, and a monetary granular limit on the same check-in type
  // used to repeat it beneath. Only granular rows that add something (a
  // non-monetary metric, or a money limit on an envelope the plan limits do not
  // name) remain, under their own heading.
  const legacyKeys = new Set(legacyLimitEntries.map(([k]) => k));
  const envelopeGranularRows = granularRows.filter(
    (row) => row.metric_type === "monetary" && row.checkin_type && !legacyKeys.has(row.checkin_type),
  );
  const otherGranularRows = granularRows.filter(
    (row) => !(row.metric_type === "monetary" && row.checkin_type && (legacyKeys.has(row.checkin_type) || envelopeGranularRows.includes(row))),
  );
  const visitCount = Number(consumptionSummary?.visit_count ?? 0);
  const isActive = Number(enrolleeData?.status) === 1 || Boolean(enrolleeData?.status);

  const categoryEntries = useMemo(
    () =>
      Object.entries(consumption?.by_category || {}).filter(([, value]) => {
        const amount = typeof value === "object" ? Number(value?.amount ?? 0) : Number(value ?? 0);
        return amount > 0;
      }),
    [consumption?.by_category],
  );

  const remainingMoney = useMemo(() => {
    const monetaryLegacy = legacyLimitEntries.find(([, value]) => value && (value.remaining != null || value.limit != null));
    if (monetaryLegacy) {
      return {
        remaining: Number(monetaryLegacy[1]?.remaining ?? 0),
        format: null,
      };
    }
    const granularMonetary = granularRows.find((row) => (
      row.metric_type === "monetary"
      || String(row.unit_label || "") === "₦"
      || row.calculation_rule === "sum_approved_cost"
    ));
    if (granularMonetary) {
      return {
        remaining: Number(granularMonetary.remaining_value ?? 0),
        format: (value) => formatGranularAmount(granularMonetary, value),
      };
    }
    return null;
  }, [granularRows, legacyLimitEntries]);

  const granularLimitLabel = (row) => {
    const parts = [
      row.metric_name || row.metric_type,
      resolveGranularTargetLabel(row),
      row.checkin_type ? String(row.checkin_type).replace(/_/g, " ") : null,
      row.period,
    ].filter(Boolean);
    return parts.join(" · ");
  };

  return (
    <>
      <Button
        type={triggerVariant === "button" ? "primary" : "link"}
        size={triggerSize}
        className={`enrollee-consumption__trigger${triggerClassName ? ` ${triggerClassName}` : ""}`}
        icon={triggerVariant === "button" ? <PieChartOutlined /> : undefined}
        onClick={openModal}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {triggerLabel}
      </Button>

      <Modal
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog enrollee-consumption-modal`}
        open={open}
        onCancel={() => setOpen(false)}
        width={820}
        destroyOnClose
        title={
          <div className="enrollee-consumption-modal__title">
            <span>Policy consumption</span>
            <Tag color={isActive ? "success" : "default"}>{isActive ? "Active" : "Inactive"}</Tag>
          </div>
        }
        footer={[
          <Button key="close" onClick={() => setOpen(false)}>
            Close
          </Button>,
          <Button key="visits" type="primary" onClick={handleVisitClick}>
            {visitCount > 0
              ? `View ${visitCount} visit${visitCount === 1 ? "" : "s"}`
              : "View encounters"}
          </Button>,
        ]}
      >
        <div className="enrollee-consumption-modal__body">
          <div className="enrollee-consumption-modal__hero">
            <img className="enrollee-consumption-modal__avatar" src={avatarSrc} alt="" />
            <div className="enrollee-consumption-modal__identity">
              <h3>{enrolleeName(enrolleeData)}</h3>
              <p>
                {userId || "—"} · Policy {consumptionSummary?.enrollee?.policy_no || enrolleeData?.policy_no || "—"}
              </p>
            </div>
          </div>

          <section className="consumption-plan-summary" aria-label="Plan cycle remaining">
            <div className="consumption-plan-summary__row">
              <span className="consumption-plan-summary__label">Plan</span>
              <span className="consumption-plan-summary__value">
                {consumptionSummary?.plan?.plan_name || enrolleeData?.plan_name || enrolleeData?.insurance_package || "—"}
              </span>
            </div>
            <div className="consumption-plan-summary__row">
              <span className="consumption-plan-summary__label">Cycle</span>
              <span className="consumption-plan-summary__value">{cycle?.label || "—"}</span>
            </div>
            <div className="consumption-plan-summary__row">
              <span className="consumption-plan-summary__label">Remaining</span>
              <span className="consumption-plan-summary__value consumption-plan-summary__value--remaining">
                {cycle?.days_remaining != null ? (
                  <Tag
                    color={
                      cycle.days_remaining > 30 ? "green" : cycle.days_remaining > 10 ? "orange" : "red"
                    }
                  >
                    {cycle.days_remaining} day{Number(cycle.days_remaining) === 1 ? "" : "s"} left
                  </Tag>
                ) : null}
                {remainingMoney ? (
                  remainingMoney.format
                    ? remainingMoney.format(remainingMoney.remaining)
                    : <CurrencyFormat value={remainingMoney.remaining} prefix="₦" thousandSeparator="," displayType="text" />
                ) : null}
                {cycle?.days_remaining == null && !remainingMoney ? "—" : null}
              </span>
            </div>
          </section>

          <div className="enrollee-consumption-modal__toolbar">
            <div className="enrollee-consumption-modal__toolbar-filters">
              <Select
                size="small"
                value={period}
                onChange={setPeriod}
                options={PERIOD_OPTIONS}
                style={{ width: 150 }}
              />
              {period === "custom" ? (
                <RangePicker
                  size="small"
                  value={customDates}
                  onChange={setCustomDates}
                  format="YYYY-MM-DD"
                />
              ) : null}
            </div>
            <div className="enrollee-consumption-modal__toolbar-actions">
              {canDownloadReport ? (
                <Tooltip
                  title={
                    customIncomplete
                      ? "Pick the start and end dates first."
                      : noReportItems
                        ? "No items were approved at PA for this client in this period."
                        : "Every item approved at PA in this period, by encounter and category."
                  }
                >
                  <span>
                    <Dropdown
                      trigger={["click"]}
                      disabled={customIncomplete || noReportItems || reportSummary == null || reportBusy}
                      menu={{
                        items: [
                          { key: "pdf", label: "PDF" },
                          { key: "xlsx", label: "Excel" },
                        ],
                        onClick: ({ key }) => downloadReport(key),
                      }}
                    >
                      <Button
                        size="small"
                        type="primary"
                        icon={<DownloadOutlined />}
                        loading={reportBusy}
                        disabled={customIncomplete || noReportItems || reportSummary == null}
                      >
                        Download report
                      </Button>
                    </Dropdown>
                  </span>
                </Tooltip>
              ) : null}
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => loadConsumption(userId)}
                loading={loading}
              >
                Reload
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="enrollee-consumption-modal__loading">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <section className="consumption-detail-section">
                <div className="consumption-detail-section__head">Envelope consumption</div>
                <div className="consumption-detail-section__body">
                  {legacyLimitEntries.length > 0
                    ? legacyLimitEntries.map(([key, value]) => (
                      <LimitProgressRow
                        key={key}
                        label={value?.label || key.replace(/_/g, " ")}
                        limit={value?.limit}
                        consumed={value?.consumed}
                        remaining={value?.remaining}
                        formatValue={pmMeasureFormatter(value?.metric) || undefined}
                        verb={pmMeasureFormatter(value?.metric) ? "used" : "spent"}
                      />
                    ))
                    : null}
                  {envelopeGranularRows.map((row) => (
                    <LimitProgressRow
                      key={String(row.id ?? `${row.metric_type}-${row.checkin_type}-${row.period}`)}
                      label={granularLimitLabel(row)}
                      tooltip={calculationRuleDescription(row.calculation_rule)}
                      limit={row.limit_value}
                      consumed={row.consumed_value}
                      remaining={row.remaining_value}
                      formatValue={(value) => formatGranularAmount(row, value)}
                    />
                  ))}
                  {legacyLimitEntries.length === 0 && envelopeGranularRows.length === 0 ? (
                    <p className="consumption-empty-hint">No envelope limits configured for this plan.</p>
                  ) : null}
                </div>
              </section>

              {otherGranularRows.length > 0 ? (
              <section className="consumption-detail-section">
                <div className="consumption-detail-section__head">Other plan limits</div>
                <div className="consumption-detail-section__body">
                  {otherGranularRows.map((row) => (
                    <LimitProgressRow
                      key={String(row.id ?? `${row.metric_type}-${row.checkin_type}-${row.period}`)}
                      label={granularLimitLabel(row)}
                      badge={(
                        <Tag color={metricTagColor(row.metric_type)} style={{ marginRight: 6 }}>
                          {row.metric_type}
                        </Tag>
                      )}
                      tooltip={calculationRuleDescription(row.calculation_rule)}
                      limit={row.limit_value}
                      consumed={row.consumed_value}
                      remaining={row.remaining_value}
                      formatValue={(value) => formatGranularAmount(row, value)}
                    />
                  ))}
                </div>
              </section>
              ) : null}

              <section className="consumption-detail-section">
                <div className="consumption-detail-section__head">This period by category</div>
                <div className="consumption-detail-section__body">
                  <div className="consumption-kv-grid">
                    {categoryEntries.map(([key, value]) => {
                      const categoryEntry = typeof value === "object" ? value : { amount: value, category_id: null };
                      const amount = Number(categoryEntry.amount || 0);
                      const categoryId = categoryEntry.category_id;
                      return (
                        <div className="consumption-kv-row" key={key}>
                          <span className="consumption-kv-row__label">{key.replace(/_/g, " ")}</span>
                          <span className="consumption-kv-row__value">
                            <CurrencyFormat value={amount} prefix="₦" thousandSeparator="," displayType="text" />
                          </span>
                          <span className="consumption-kv-row__action">
                            <ConsumedServices
                              userId={userId}
                              categoryId={categoryId}
                              categoryName={key.replace(/_/g, " ")}
                              onVisitClick={handleVisitClick}
                              disabled={!(amount > 0)}
                              period={period}
                              customDates={period === "custom" ? customDates : null}
                            />
                          </span>
                        </div>
                      );
                    })}
                    {categoryEntries.length === 0 ? (
                      <p className="consumption-empty-hint">No category consumption in this period.</p>
                    ) : null}
                  </div>
                  <div className="consumption-kv-total">
                    <span>Total this period</span>
                    <span>
                      <CurrencyFormat
                        value={consumption?.total || 0}
                        prefix="₦"
                        thousandSeparator=","
                        displayType="text"
                      />
                    </span>
                  </div>
                </div>
              </section>

              <section className="consumption-detail-section" aria-label="Consumption by benefit item">
                <div className="consumption-detail-section__head">This period by benefit item</div>
                <div className="consumption-detail-section__body">
                  {benefitItemRows.map((row) => (
                    <LimitProgressRow
                      key={row.benefit_item_id}
                      label={row.name}
                      badge={row.pool_name && row.shared_pool ? (
                        <Tooltip title={`Shares the "${row.pool_name}" limit with other benefit items; remaining is the shared balance.`}>
                          <Tag style={{ marginRight: 6 }}>shared limit</Tag>
                        </Tooltip>
                      ) : null}
                      tooltip={`${row.lines} line${Number(row.lines) === 1 ? "" : "s"} mapped to this benefit item in the period`
                        + (pmMeasureFormatter(row.metric) && row.amount != null ? ` · ₦${Number(row.amount).toLocaleString("en-NG")} billed` : "")}
                      limit={row.limit}
                      consumed={row.consumed}
                      remaining={row.remaining}
                      formatValue={pmMeasureFormatter(row.metric) || undefined}
                      verb={pmMeasureFormatter(row.metric) ? "used" : "spent"}
                    />
                  ))}
                  {benefitItemRows.length === 0 ? (
                    <p className="consumption-empty-hint">
                      No mapped benefit items in this period. Items appear here once their tariff names are mapped to Benefit Items.
                    </p>
                  ) : null}
                </div>
              </section>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default EnrolleeConsumption;

export const ConsumedServices = ({
  userId,
  categoryId,
  categoryName = null,
  disabled = false,
  period = "cycle",
  customDates = null,
  onVisitClick = null,
}) => {
  const [showData, setShowData] = useState(false);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({});
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const openModal = async () => {
    if (!categoryId) {
      notification.warning({ message: "Category ID not available" });
      return;
    }
    setShowData(true);
    await fetchItems(userId, categoryId);
  };

  const closeModal = () => {
    setShowData(false);
    setData([]);
    setTotals({});
    setCategoryInfo(null);
  };

  const fetchItems = async (insuranceNo, catId) => {
    try {
      setLoading(true);
      const params = { period, category_id: catId };
      if (period === "custom" && customDates && customDates[0] && customDates[1]) {
        params.start_date = customDates[0].format("YYYY-MM-DD");
        params.end_date = customDates[1].format("YYYY-MM-DD");
      }
      const resp = await lib.getEnrolleeConsumedItems(insuranceNo, params);
      const body = resp?.data;
      if (body && Number(body?.error) === 0) {
        const responseData = body?.data || {};
        setData(Array.isArray(responseData.items) ? responseData.items : []);
        setTotals(responseData.totals || {});
        setCategoryInfo(responseData.category || null);
      } else {
        notification.error({ message: body?.message || "Failed to load consumed items" });
      }
    } catch (error) {
      console.error("Error fetching consumed items:", error);
      notification.error({ message: "An error occurred while fetching data" });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: "Item ID", dataIndex: "item_id", key: "item_id", width: 80 },
    { title: "Item", dataIndex: "item_name", key: "item_name", ellipsis: true },
    { title: "Code", dataIndex: "item_code", key: "item_code", width: 110, ellipsis: true },
    { title: "Brand", dataIndex: "item_brand", key: "item_brand", width: 100, render: (v) => v || "—" },
    {
      title: "Cost",
      dataIndex: "total_cost",
      key: "total_cost",
      width: 110,
      align: "right",
      render: (v) => <CurrencyFormat value={v} prefix="₦" thousandSeparator="," displayType="text" />,
    },
    {
      title: "Approved",
      dataIndex: "total_approved_cost",
      key: "total_approved_cost",
      width: 110,
      align: "right",
      render: (v) => <CurrencyFormat value={v} prefix="₦" thousandSeparator="," displayType="text" />,
    },
    { title: "Uses", dataIndex: "total_times", key: "total_times", width: 72, align: "right" },
    {
      title: "Last used",
      dataIndex: "last_used",
      key: "last_used",
      width: 120,
      render: (v) => v || "—",
    },
  ];

  return (
    <>
      <Button type="link" size="small" disabled={disabled} onClick={openModal} className="listing-card__action">
        Items
      </Button>
      <Modal
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog consumed-items-modal`}
        centered
        width={1100}
        style={{ maxWidth: "96vw" }}
        title={`Consumed items — ${categoryInfo?.name || categoryName}`}
        open={showData}
        destroyOnClose
        onCancel={closeModal}
        footer={<Button onClick={closeModal}>Close</Button>}
      >
        {!loading && data.length > 0 ? (
          <div className="consumed-items-modal__summary">
            <div>
              <strong>Category</strong>
              <Tag color="blue" style={{ marginLeft: 6 }}>
                {categoryInfo?.name || categoryName}
              </Tag>
            </div>
            <div>
              <strong>Cost</strong>
              <CurrencyFormat value={totals?.total_cost || 0} prefix="₦" thousandSeparator="," displayType="text" />
            </div>
            <div>
              <strong>Approved</strong>
              <CurrencyFormat
                value={totals?.total_approved_cost || 0}
                prefix="₦"
                thousandSeparator=","
                displayType="text"
              />
            </div>
            <div>
              <strong>Visits</strong>
              <span
                style={
                  onVisitClick
                    ? { color: "#3497D3", cursor: "pointer", textDecoration: "underline" }
                    : undefined
                }
                onClick={onVisitClick || undefined}
              >
                {totals?.total_visits || 0}
              </span>
              <span> · {data.length} items</span>
            </div>
          </div>
        ) : null}

        <Table
          className="search-results-table"
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey="item_id"
          size="small"
          locale={{ emptyText: "No items found for this category." }}
          scroll={{ x: "max-content" }}
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
        />
      </Modal>
    </>
  );
};
